import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import {
    IAttributeConfData,
    IAttributeConfDataOption
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { DfEventService } from '@df/core/service/df-event';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { BasketService } from '@df/session/api/basket/basket.service';
import { DfUiModule } from '@df/ui/df-ui.module';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { LocalProduct } from '../../../../model/product/product.decorator';
import { NNotifyMeModal } from '../../notify-me-modal/product-notify-me-modal';

export const ADD_TO_BASKET_WITHOUT_OPTION_EVENT = 'addToBasketWithoutOption';
declare module '@df/core/service/df-event' {
    interface IEventType {
        // addToBasketWithoutOption, attr code as data
        addToBasketWithoutOption: DfEventConfig<
            'addToBasketWithoutOption',
            { attrCode: string; product?: LocalProduct<IProductDataV2> },
            void
        >;
    }
}

@Component({
    selector: 'product-configurable-option-dropdown',
    templateUrl: './product-configurable-option-dropdown.html',
    styleUrls: ['./product-configurable-option-dropdown.scss'],
    standalone: true,
    imports: [OffClickModule, TranslationModule, ToggleModule, DfUiModule, HideUpComponent, NgTemplateOutlet, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductConfigurableOptionDropdownComponent extends UiComponent {
    /**
     * Selected value
     */
    @Input()
    value?: any;

    /**
     * If is multiple variant (dont show oos and configurable errors)
     */
    @Input()
    multipleVariant = false;

    /**
     * If all options are selected
     */
    @Input()
    displayWarnings = true;

    /**
     * Value output
     */
    @Output()
    valueChange = new EventEmitter<any>();

    /**
     * Attr with options
     */
    @Input({ required: true })
    attr!: IAttributeConfData<LocalProduct<IProductDataV3>>;

    /**
     * If is open
     */
    openState = new BehaviorSubject(false);

    private basketService = inject(BasketService);
    private dfModalService = inject(DfModalService);
    private dfEventService = inject(DfEventService);
    private scrollSubscription?: Subscription;
    protected isNotifyMeOpen = false;

    private waitForSelectionResolve!: ((product?: LocalProduct<IProductDataV2>) => void) | undefined;

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.dfEventService.before(event => {
                if (event.id === ADD_TO_BASKET_WITHOUT_OPTION_EVENT && event.data.attrCode === this.attr.code) {
                    this.toggle(true);
                    return new Promise<void>(resolve => {
                        this.waitForSelectionResolve = (product?: LocalProduct<IProductDataV2>) => {
                            event.data.product = product;
                            if (!product) {
                                event.action = undefined;
                            }
                            resolve();
                        };
                    });
                }
            })
        );
    }

    /**
     * Toggle state
     */
    toggle(to = !this.openState.value) {
        if (to !== this.openState.value && !!this.options.length) {
            this.openState.next(to);
            this.detectChanges();
            if (!to && !!this.waitForSelectionResolve) {
                this.waitForSelectionResolve();
            }
            if (to && this.client.sizeId < 2) {
                this.scrollSubscription = this.client.obs.scroll.subscribe(() => {
                    this.toggle(false);
                });
            } else {
                this.scrollSubscription?.unsubscribe();
            }
        }
    }

    override onBreakpoint() {
        this.toggle(false);
        if (this.scrollSubscription && this.client.sizeId >= 2) {
            this.scrollSubscription.unsubscribe();
        }
        if (!this.scrollSubscription && this.client.sizeId < 2) {
            this.scrollSubscription = this.client.obs.scroll.subscribe(() => {
                this.toggle(false);
            });
        }
    }

    pick(option: IAttributeConfDataOption<LocalProduct<IProductDataV3>>) {
        const isNotifyMe = this.isNotifyMe(option);
        if (isNotifyMe) {
            this.openNotify(option);
        }
        if (option !== this.value) {
            this.valueChange.emit((this.value = option));
            if (!isNotifyMe) {
                if (this.waitForSelectionResolve) {
                    this.waitForSelectionResolve(option.product);
                    this.waitForSelectionResolve = undefined;
                }
                this.toggle(false);
            }
            this.detectChanges();
        }
    }

    protected isOutOfStock(option: IAttributeConfDataOption<LocalProduct<IProductDataV3>>): boolean {
        return !this.displayWarnings
            ? false
            : !this.basketService.isSalable(this.basketService.normalizeAddToBasketData({ id: option.product.id, qty: 1 }));
    }

    protected isNotifyMe(option: IAttributeConfDataOption<LocalProduct<IProductDataV3>>): boolean {
        if (!this.displayWarnings) {
            return false;
        }
        const product = option?.product as unknown as LocalProduct<IProductDataV2>;
        return product?.isNotifyMe;
    }

    protected productsLeft(option: IAttributeConfDataOption<LocalProduct<IProductDataV3>>): number {
        const stock = this.basketService.maxQty(this.basketService.normalizeAddToBasketData({ id: option.product.id, qty: 1 }), 1);
        return stock > 0 ? stock : 0;
    }

    protected openNotify(option: IAttributeConfDataOption<LocalProduct<IProductDataV3>>) {
        this.dfModalService.open(
            undefined,
            NNotifyMeModal.BLOCK_ID,
            undefined,
            {
                product: option.product
            },
            modalRef => {
                this.scrollSubscription?.unsubscribe();
                this.isNotifyMeOpen = true;
                this.detectChanges();

                // Afte close notify me modal make offclick work and scroll close
                modalRef
                    .afterClosed()
                    .pipe(first())
                    .subscribe(() => {
                        this.isNotifyMeOpen = false;
                        this.detectChanges();
                        if (this.client.sizeId < 2) {
                            this.scrollSubscription = this.client.obs.scroll.subscribe(() => {
                                this.toggle(false);
                            });
                        }
                    });
            }
        );
    }

    get options() {
        return this.attr.options;
    }

    // display dropdown if more then one size or notify me or out of stock
    get canBeDisplayed() {
        return (
            !this.attr.hidden ||
            (this.options?.[0] && (this.isNotifyMe(this.options[0]) || this.isOutOfStock(this.options[0]))) ||
            this.multipleVariant
        );
    }
}
